import { useQuery } from '@tanstack/react-query';
import { projectsApi, teamsApi, tasksApi, usersApi, analyticsApi, dashboardApi } from '@/api';
import { queryKeys, type ProjectListParams } from '@/query/queryKeys';

// ---------------------------------------------------------------------------
// React Query hooks -- PRIMARY SOURCE OF TRUTH for server data.
//
// These hooks are the only shared source of truth for server collections.
// Mutations update these caches through query/cacheUpdates.ts and then
// invalidate the corresponding key hierarchy for authoritative background
// reconciliation.
//
// Query keys used across the app:
//   ['projects', params?]   - project list
//   ['project', id]         - single project with sections/members/custom fields
//   ['projectTasks', id]    - tasks for a specific project
//   ['task', id]            - single task detail
//   ['teams']               - team list
//   ['myTasks']             - current user's tasks
//   ['users']               - all users
//   ['analyticsOverview']   - analytics overview
//   ['dashboardLayout']     - dashboard widget layout
// ---------------------------------------------------------------------------

/** Fetches all projects (with optional pagination/filter params) */
export function useProjects(params?: ProjectListParams) {
  return useQuery({
    queryKey: queryKeys.projects.list(params),
    queryFn: () => projectsApi.getAll(params),
    staleTime: 30_000, // 30 seconds
  });
}

/** Fetches every project accessible to the current user for complete selectors and access checks. */
export function useAllProjects() {
  return useQuery({
    queryKey: queryKeys.projects.complete,
    queryFn: () => projectsApi.getAllAccessible(),
    staleTime: 30_000,
  });
}

/** Fetches a single project by ID (with sections, members, custom fields) */
export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.projects.detail(id),
    queryFn: () => projectsApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** Fetches tasks for a specific project */
export function useProjectTasks(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.tasks.project(projectId),
    queryFn: () => tasksApi.getAllByProject(projectId!),
    enabled: !!projectId,
    staleTime: 15_000,
  });
}

/** Fetches all teams */
export function useTeams() {
  return useQuery({
    queryKey: queryKeys.teams.all,
    queryFn: () => teamsApi.getAll(),
    staleTime: 60_000, // 1 minute
  });
}

/** Fetches the current user's tasks */
export function useMyTasks() {
  return useQuery({
    queryKey: queryKeys.tasks.my,
    queryFn: () => tasksApi.getMyTasks(),
    staleTime: 15_000, // 15 seconds
  });
}

/** Fetches every user in the organization for complete selectors. */
export function useAllUsers() {
  return useQuery({
    queryKey: queryKeys.users.complete,
    queryFn: () => usersApi.getAllAccessible(),
    staleTime: 60_000,
  });
}

/** Fetches analytics overview when the current role is allowed to view it. */
export function useAnalyticsOverview(enabled = true) {
  return useQuery({
    queryKey: queryKeys.analytics.overview,
    queryFn: () => analyticsApi.getOverview(),
    staleTime: 60_000,
    enabled,
  });
}

/** Fetches dashboard layout */
export function useDashboardLayout() {
  return useQuery({
    queryKey: queryKeys.dashboard.layout,
    queryFn: () => dashboardApi.getLayout(),
    staleTime: 300_000, // 5 minutes
  });
}
